import { defineStore } from 'pinia';
import { ref, shallowRef } from 'vue';
import type { FileResource } from '../types/resource';

export interface FilemanagerUploadDestination
{
    scope: string;
    folder: number | null;
    name: string;
}

export interface FilemanagerUploadResult
{
    owner: string;
    destination: FilemanagerUploadDestination;
    items: FileResource[];
}

export const useFilemanagerUploadStore = defineStore('core-filemanager-upload', () =>
{
    const files = shallowRef<File[]>([]);
    const preparationOpen = ref(false);
    const busy = ref(false);
    const percent = ref(0);
    const owner = ref('');
    const destination = ref<FilemanagerUploadDestination>({ scope: 'public', folder: null, name: '' });
    const completed = shallowRef<FilemanagerUploadResult | null>(null);
    const progressRequest = ref(0);
    const activeBatch = shallowRef<Omit<FilemanagerUploadResult, 'items'> | null>(null);

    function showProgress()
    {
        progressRequest.value++;
    }

    function prepare(location: FilemanagerUploadDestination, manager: string)
    {
        if (busy.value)
        {
            showProgress();
            return;
        }
        if (!files.value.length)
        {
            destination.value = { ...location };
            owner.value = manager;
        }
        preparationOpen.value = true;
    }

    function setFiles(value: File[], location: FilemanagerUploadDestination, manager: string)
    {
        if (busy.value) return;
        prepare(location, manager);
        files.value = value;
    }

    function start()
    {
        activeBatch.value = { owner: owner.value, destination: { ...destination.value } };
        busy.value = true;
        preparationOpen.value = false;
    }

    function finish(items: FileResource[])
    {
        if (items.length && activeBatch.value)
            completed.value = { ...activeBatch.value, items: [...items] };
        busy.value = false;
    }

    return { files, preparationOpen, busy, percent, owner, destination, completed, progressRequest, activeBatch, prepare, setFiles, start, finish, showProgress };
});
